import React, { useCallback } from 'react';

import { FontAwesomeIcon, Input } from '@appcharge/shared-ui';

import CustomizedSwitch from 'components/SwitchButton/SwitchButton';

import { ExtendedIntegrationData } from '../DeveloperSettings.types';

import { IPasswordInputIconAction, PasswordInput } from './PasswordInput';
import { SettingsCard } from './SettingsCard';

interface ICredentialsSectionProps {
  integrationData: ExtendedIntegrationData | undefined;
  isToggleInProgress: boolean;
  onCopy: (value: string, label: string) => void;
  onToggleJwtSecret: () => void;
}

export const CredentialsSection: React.FC<ICredentialsSectionProps> = ({
  integrationData,
  isToggleInProgress,
  onCopy,
  onToggleJwtSecret
}) => {
  const isDeeplinkJwtSecretSettingsVisible =
    integrationData?.isDeeplinkJwtSecretSettingsEnabled === true;

  const handleCopyMainKey = useCallback((): void => {
    onCopy(integrationData?.apiUrlPrimaryKey || '', 'Main Key');
  }, [onCopy, integrationData?.apiUrlPrimaryKey]);

  const handleCopyCheckoutPublicKey = useCallback((): void => {
    onCopy(integrationData?.checkoutPublicKey || '', 'Checkout Public Key');
  }, [onCopy, integrationData?.checkoutPublicKey]);

  const handleCopyPublisherToken = useCallback((): void => {
    onCopy(integrationData?.publisherToken || '', 'Publisher Token');
  }, [onCopy, integrationData?.publisherToken]);

  const handleCopyDeeplinkJwtSecret = useCallback((): void => {
    onCopy(integrationData?.deeplinkJwtSecret || '', 'Deeplink JWT Secret');
  }, [onCopy, integrationData?.deeplinkJwtSecret]);

  return (
    <SettingsCard
      title="Credentials"
      description="Keys and tokens required to authenticate your API and SDK requests."
    >
      <div className="grid grid-cols-2 gap-4">
        <PasswordInput
          label="Main Key"
          value={integrationData?.apiUrlPrimaryKey || ''}
          iconActions={createCopyIconActions(handleCopyMainKey)}
        />
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Checkout Public Key
          </label>
          <Input
            value={integrationData?.checkoutPublicKey || ''}
            disabled
            dataTestId="credentials-checkout-public-key"
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-copy"
                onClick={handleCopyCheckoutPublicKey}
              />
            }
            rightIconStyles="cursor-pointer text-base-mutedForeground"
          />
        </div>
        <PasswordInput
          label="Publisher Token"
          value={integrationData?.publisherToken || ''}
          iconActions={createCopyIconActions(handleCopyPublisherToken)}
        />
      </div>

      {isDeeplinkJwtSecretSettingsVisible && (
        <div className="flex gap-12 items-end mt-6 border-t border-gray-100 pt-6">
          <div className="mb-4 flex items-center">
            <CustomizedSwitch
              status={!!integrationData?.deeplinkOn}
              texts={[]}
              disabled={isToggleInProgress}
              functions={[onToggleJwtSecret, onToggleJwtSecret]}
            />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Generate JWT Secret
              </p>
              <p className="text-[13px] text-gray-500">
                Generate a secret key used to securely sign and verify JWT
                tokens.
              </p>
            </div>
          </div>
          {integrationData?.deeplinkOn && (
            <div className="flex-1">
              <PasswordInput
                label="Deeplink JWT Secret"
                value={integrationData?.deeplinkJwtSecret || ''}
                iconActions={createCopyIconActions(handleCopyDeeplinkJwtSecret)}
              />
            </div>
          )}
        </div>
      )}
    </SettingsCard>
  );
};

const createCopyIconActions = (
  onClick: () => void
): IPasswordInputIconAction[] => [
  {
    icon: 'fa-regular fa-copy',
    onClick
  }
];
